import ReactDOM from "react-dom";
import {Highlight} from "react-instantsearch";
import React from "react";
import { sanitize } from "../Fiches/RichTextEditor";

// Définition / Note / Commentaire sont saisis en WYSIWYG (RichTextEditor) et
// stockés en HTML : on les affiche donc comme du HTML, nettoyé ici avec la
// même liste blanche qu'à la saisie (défense en profondeur : le contenu peut
// aussi venir de données legacy ou d'un autre point d'écriture que le formulaire).
function sanitizedHtml(html) {
    return { __html: sanitize(html) };
}

/**
 * ModalFiche — composant réutilisable
 *
 * Usage :
 *   <ModalFiche hit={selectedHit} onClose={() => setSelectedHit(null)} />
 *
 * Props :
 *   - hit      : l'objet Algolia (ou null pour ne pas afficher)
 *   - onClose  : callback appelé au clic sur "Fermer" ou le backdrop
 */
/**
 * Formatte les domaines/sous-domaines d'une fiche :
 *   - plusieurs domaines sont joints par " - " (ex. "Nucléaire - Astronomie")
 *   - plusieurs sous-domaines sont joints par " et "
 *   - domaines et sous-domaines sont séparés par " / " quand les deux existent
 */
function formatDateFr(date) {
    return new Date(date).toLocaleDateString('fr-FR');
}

function formatDomaine(domaine, sousDomaine) {
    const domaines = (Array.isArray(domaine) ? domaine : [domaine]).filter(Boolean);
    const sousDomaines = (Array.isArray(sousDomaine) ? sousDomaine : [sousDomaine]).filter(Boolean);

    const domainesStr = domaines.join(' - ');
    const sousDomainesStr = sousDomaines.join(' et ');

    if (!domainesStr) return sousDomainesStr;
    return sousDomainesStr ? `${domainesStr} / ${sousDomainesStr}` : domainesStr;
}

// Libellé FORME_VAR_FRV (FRV_ID=3) utilisé par FicheIndexer pour peupler varTypeLib
const FORME_DEVELOPPEE_LIB = "Forme développée";

/**
 * Formatte les variantes "Forme développée" d'une fiche : plusieurs formes
 * développées (une par terme) sont jointes par " ; ".
 */
function formatFormeDeveloppee(variantes) {
    if (!Array.isArray(variantes)) return null;
    const formes = variantes
        .filter(v => v?.varTypeLib === FORME_DEVELOPPEE_LIB && v?.varTerme)
        .map(v => v.varTerme);
    return formes.length > 0 ? formes.join(' ; ') : null;
}

/**
 * Highlight Algolia d'un attribut, avec repli sur sa valeur brute quand le
 * hit ne porte pas les métadonnées _highlightResult (ex. un aperçu construit
 * localement depuis un formulaire, hors contexte de recherche).
 */
function FieldValue({ hit, attribute }) {
    if (hit?._highlightResult) {
        return <Highlight attribute={attribute} hit={hit} />;
    }
    const value = hit?.[attribute];
    return Array.isArray(value) ? value.join(', ') : (value ?? '');
}

/**
 * FicheApercuBody — récap d'une fiche (contenu réutilisé par ModalFiche en
 * popup de recherche et par l'aperçu inline d'Ajout Terme).
 *
 * Props :
 *   - hit     : objet au format Algolia (ou un objet construit localement
 *               avec les mêmes clés : terme_m, domaine, sous_domaine,
 *               definition, note, variantes, voir, langue_etrangere,
 *               a_interroger, commentaire, source, fic_num, commission, date)
 *   - visible : affiche ou masque le composant (défaut: true). Permet de
 *               l'appeler depuis plusieurs emplacements sans que chaque
 *               appelant ait à conditionner son rendu lui-même.
 */
export function FicheApercuBody({ hit, visible = true }) {
    if (!visible || !hit) return null;

    const formeDeveloppee = formatFormeDeveloppee(hit?.variantes);

    return (
        <>
            {(hit?.fic_num || hit?.commission || hit?.date) && (
                <div className="text-end text-muted small mb-3">
                    <strong>{hit?.fic_num}</strong><br />
                    {hit?.commission}<br />
                    {hit?.date && <strong>{`Créé le ${formatDateFr(hit.date)}`}</strong>}
                    {hit?.date && hit?.date_suggestion && <br />}
                    {hit?.date_suggestion && `Suggéré le ${formatDateFr(hit.date_suggestion)}`}
                </div>
            )}

            <div><b>{hit?.terme_m || "Aucun Terme"}</b></div>
            {/*<div>{hit?.terme_f}</div>*/}

            {(hit?.domaine?.length > 0 || hit?.sous_domaine?.length > 0) && (
                <div><i>Domaines :</i> {formatDomaine(hit?.domaine, hit?.sous_domaine)}</div>
            )}

            {formeDeveloppee && (
                <div><i>Forme développée :</i> {formeDeveloppee}</div>
            )}

            {hit?.definition && (
                <div><i>Définition :</i> <span dangerouslySetInnerHTML={sanitizedHtml(hit.definition)} /></div>
            )}

            {hit?.note && (
                <div><i>Note :</i> <span dangerouslySetInnerHTML={sanitizedHtml(hit.note)} /></div>
            )}

            {hit?.voir && (
                <div>
                    <i>Voir aussi :</i>{' '}
                    {hit.voir.split(',').map((item, index, array) => {
                        // 1. On découpe chaque morceau par le double underscore
                        const [libelle, ficId] = item.trim().split('__');

                        // On vérifie que les deux parties existent pour éviter les erreurs de rendu
                        if (!libelle || !ficId) return null;

                        return (
                            <span key={ficId}>
                                              <a
                                                  href={`fiche__${ficId}`}
                                                  className="text-blue-600 hover:underline"
                                              >
                                                {libelle}
                                              </a>
                                {/* Ajoute un espace ou une virgule entre les liens */}
                                {index < array.length - 1 && ' '}
        </span>
                        );
                    })}
                </div>
            )}

            {hit?.langue_etrangere?.length > 0 && (
                <div>
                    <i>Équivalent étranger :</i>{" "}
                    <FieldValue attribute="langue_etrangere" hit={hit} />
                </div>
            )}

            {hit?.a_interroger && (
                <div>
                    <i>À interroger :</i>{" "}
                    <FieldValue attribute="a_interroger" hit={hit} />
                </div>
            )}

            {hit?.commentaire && (
                <div>
                    <i>Commentaire :</i>{" "}
                    <span dangerouslySetInnerHTML={sanitizedHtml(hit.commentaire)} />
                </div>
            )}

            {hit?.source && (
                <div className="text-end">
                    <i>Source :</i> {hit?.source}
                </div>
            )}
        </>
    );
}

export function ModalFiche({ hit, onClose }) {
    if (!hit) return null;

    return ReactDOM.createPortal(
        <>
            <div
                className="modal modal-blur fade show d-block"
                tabIndex="-1"
                role="dialog"
                style={{ zIndex: 2000 }}
            >
                <div className="modal-dialog modal-lg modal-dialog-centered">
                    <div className="modal-content">

                        {/* HEADER */}
                        <div className="modal-header">
                            <h5 className="modal-title">
                                {hit.terme_m}
                                {hit.gramm_cat && `, ${hit.gramm_cat}`}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onClose}
                            />
                        </div>

                        {/* BODY */}
                        <div className="modal-body">
                            <FicheApercuBody hit={hit} />
                        </div>

                        {/* FOOTER */}
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onClose}
                            >
                                Fermer
                            </button>
                        </div>

                    </div>
                </div>
            </div>

            {/* BACKDROP */}
            <div
                className="modal-backdrop fade show"
                style={{ zIndex: 1999 }}
                onClick={onClose}
            />
        </>,
        document.body
    );
}
